// mobile_adapter.js — OpenDraw Mobile Adapter (Functional / Robust)
// - Modals open/close
// - Modal buttons proxy-click real desktop controls (no window.setTool needed)
// - Pointer→Mouse bridge (canvas + window) so arrows/handlers drag works on touch
// - Does NOT touch desktop behavior

(() => {
  const isMobile =
    window.matchMedia("(max-width: 900px)").matches ||
    window.matchMedia("(pointer: coarse)").matches;

  if (!isMobile) return;

  window.__odMobileAdapter = true;

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  // ---------------------------
  // Modal helpers (global)
  // ---------------------------
  window.openModal = function openModal(id) {
    const el = document.getElementById(id);
    if (!el) return;
    el.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("od-modal-open");
    document.body.style.overflow = "hidden";
  };

  window.closeModal = function closeModal(id) {
    const el = document.getElementById(id);
    if (!el) return;
    el.setAttribute("aria-hidden", "true");
    const anyOpen = $$('.modal[aria-hidden="false"]').length > 0;
    if (!anyOpen) {
      document.documentElement.classList.remove("od-modal-open");
      document.body.style.overflow = "";
    }
  };

  // ---------------------------
  // Proxy helpers
  // ---------------------------
  function proxyClick(selector) {
    const el = $(selector);
    if (el) el.click();
    return !!el;
  }

  // ---------------------------
  // Close wiring
  // ---------------------------
  function bindModalClose() {
    $$("[data-close-modal]").forEach((btn) => {
      if (btn.__odBound) return;
      btn.__odBound = true;

      btn.addEventListener(
        "click",
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          const id = btn.getAttribute("data-close-modal");
          window.closeModal(id);
        },
        { passive: false }
      );
    });

    // ESC closes any open modal
    if (!window.__odModalEscBound) {
      window.__odModalEscBound = true;
      window.addEventListener("keydown", (e) => {
        if (e.key !== "Escape") return;
        $$('.modal[aria-hidden="false"]').forEach((m) => window.closeModal(m.id));
      });
    }
  }

  // ---------------------------
  // Toolbar wiring
  // ---------------------------
  function bindToolbarButtons() {
    const wire = (id, fn) => {
      const b = document.getElementById(id);
      if (!b || b.__odBound) return;
      b.__odBound = true;
      b.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        fn();
      }, { passive: false });
    };

    wire("mUndoBtn", () => proxyClick("#undoBtn"));
    wire("mPdfBtn",  () => proxyClick("#exportPdfBtn"));
    wire("mBallBtn", () => proxyClick("#addBallBtn"));

    // FIX 1: ambos botones de frame deben usar smartFrameBtn/smartFramePillBtn,
    // NO addFrameBtn (que agrega un frame vacío sin contenido inicial).
    // Fallback a addFrameBtn solo si ninguno de los smart existe.
    const addSmartFrame = () => {
      const ok =
        proxyClick("#smartFrameBtn") ||
        proxyClick("#smartFramePillBtn");
      if (!ok) proxyClick("#addFrameBtn"); // fallback last resort
    };

    wire("mFrameBtn",    addSmartFrame);
    wire("mAddFrameBtn", addSmartFrame);

    wire("mDeleteBtn", () => {
      document.dispatchEvent(new KeyboardEvent("keydown", { key: "Delete", bubbles: true }));
    });

    wire("mSelectBtn", () => {
      const ok =
        proxyClick('#selectArrowBtn') ||
        proxyClick('.toolItem[data-tool="select"]');
      if (!ok) document.dispatchEvent(new KeyboardEvent("keydown", { key: "v", bubbles: true }));
    });

    wire("mArrowsBtn", () => window.openModal("arrowsModal"));
    wire("mIconsBtn",  () => window.openModal("iconsModal"));
    wire("mColorsBtn", () => window.openModal("colorsModal"));
    wire("mPathBtn",   () => window.openModal("pathModal"));

    // FIX 3: back button → volver al organizer
    wire("mBackBtn", () => {
      // Intentar ir a la URL guardada en localStorage (mismo patrón que od_topbar.js)
      let href = "./organizer.html";
      try {
        const saved = localStorage.getItem("od_organizer_href") || "";
        if (saved) href = saved;
      } catch {}
      window.location.href = href;
    });
  }

  // ---------------------------
  // Modal actions
  // ---------------------------
  function bindModalActions() {
    // Arrows modal
    const arrowsModal = document.getElementById("arrowsModal");
    if (arrowsModal && !arrowsModal.__odBound) {
      arrowsModal.__odBound = true;
      arrowsModal.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-tool]");
        if (!btn) return;
        const tool = btn.getAttribute("data-tool");
        if (tool) proxyClick(`.toolItem[data-tool="${tool}"]`);
        if (btn.hasAttribute("data-close-on-select")) window.closeModal("arrowsModal");
      }, { passive: false });
    }

    // Colors modal
    const colorsModal = document.getElementById("colorsModal");
    if (colorsModal && !colorsModal.__odBound) {
      colorsModal.__odBound = true;
      colorsModal.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-color]");
        if (!btn) return;
        const color = btn.getAttribute("data-color");
        if (color) proxyClick(`.colorSwatch[data-color="${cssEscape(color)}"]`);
        window.closeModal("colorsModal");
      }, { passive: false });
    }

    // Path modal
    const pathModal = document.getElementById("pathModal");
    if (pathModal && !pathModal.__odBound) {
      pathModal.__odBound = true;
      pathModal.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-mode]");
        if (!btn) return;
        const mode = btn.getAttribute("data-mode");
        if (mode === "curve") proxyClick("#modeCurveBtn");
        else if (mode === "straight") proxyClick("#modeStraightBtn");
        window.closeModal("pathModal");
      }, { passive: false });
    }

    // Icons modal
    const iconsModal = document.getElementById("iconsModal");
    if (iconsModal && !iconsModal.__odBound) {
      iconsModal.__odBound = true;
      iconsModal.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-icon]");
        if (!btn) return;
        const icon = btn.getAttribute("data-icon");
        if (!icon) return;
        proxyClick(`.iconBtn[data-icon="${cssEscape(icon)}"]`);
        window.closeModal("iconsModal");
      }, { passive: false });
    }
  }

  // ---------------------------
  // Icon sync
  // ---------------------------
  function syncIconsToModal() {
    const grid = document.getElementById("iconGrid");
    const modalGrid = document.getElementById("iconGridModal");
    if (!grid || !modalGrid) return;

    const srcButtons = grid.querySelectorAll("button.iconBtn, button[data-icon]");
    if (!srcButtons.length) return;
    if (modalGrid.__odIconSig === srcButtons.length) return;
    modalGrid.__odIconSig = srcButtons.length;

    modalGrid.innerHTML = "";
    srcButtons.forEach((b) => {
      const icon = b.getAttribute("data-icon") || b.dataset.icon;
      const clone = b.cloneNode(true);
      clone.className = "iconBtnModal";
      clone.removeAttribute("id");
      clone.setAttribute("data-icon", icon || "");

      clone.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        b.click();
        window.closeModal("iconsModal");
      }, { passive: false });

      modalGrid.appendChild(clone);
    });
  }

  // ---------------------------
  // Pointer → Mouse bridge
  // ---------------------------
  function bridgePointerToMouse(canvas) {
    if (!canvas || canvas.__odPointerBridge) return;
    canvas.__odPointerBridge = true;

    canvas.style.touchAction = "none";

    let activePointerId = null;

    const dispatchMouse = (target, type, pe) => {
      const ev = new MouseEvent(type, {
        bubbles: true,
        cancelable: true,
        clientX: pe.clientX,
        clientY: pe.clientY,
        button: 0,
      });
      target.dispatchEvent(ev);
    };

    canvas.addEventListener("pointerdown", (pe) => {
      if (pe.pointerType === "mouse") return;
      activePointerId = pe.pointerId;
      try { canvas.setPointerCapture(activePointerId); } catch {}
      pe.preventDefault();
      dispatchMouse(canvas, "mousedown", pe);
    }, { passive: false });

    canvas.addEventListener("pointermove", (pe) => {
      if (pe.pointerType === "mouse") return;
      if (activePointerId !== pe.pointerId) return;
      pe.preventDefault();
      dispatchMouse(canvas, "mousemove", pe);
      dispatchMouse(window, "mousemove", pe);
    }, { passive: false });

    const end = (pe) => {
      if (pe.pointerType === "mouse") return;
      if (activePointerId !== pe.pointerId) return;
      pe.preventDefault();
      dispatchMouse(canvas, "mouseup", pe);
      dispatchMouse(window, "mouseup", pe);
      try { canvas.releasePointerCapture(activePointerId); } catch {}
      activePointerId = null;
    };

    canvas.addEventListener("pointerup",     end, { passive: false });
    canvas.addEventListener("pointercancel", end, { passive: false });
  }

  function bridgeAllCurrentCanvases() {
    $$("canvas").forEach((cv) => bridgePointerToMouse(cv));
  }

  // ---------------------------
  // Utils
  // ---------------------------
  function cssEscape(v) {
    return String(v).replace(/"/g, '\\"');
  }

  // ---------------------------
  // FIX 2: Frame navigation — contador que se mantiene vivo
  // ---------------------------
  function setupFrameNavigation() {
    const grid    = document.getElementById("framesGrid");
    const prevBtn = document.getElementById("navPrev");
    const nextBtn = document.getElementById("navNext");
    const metaEl  = document.getElementById("navMeta");

    if (!grid || !prevBtn || !nextBtn || !metaEl) return;

    let currentIdx = 0;

    function getCards() {
      // Excluye placeholders/insert-lines que no son frames reales
      return Array.from(grid.querySelectorAll(".frameCard"));
    }

    function updateMeta() {
      const cards = getCards();
      const total = cards.length;

      if (total === 0) {
        metaEl.textContent = "0 / 0";
        prevBtn.disabled = true;
        nextBtn.disabled = true;
        return;
      }

      // Clamp por si se borró el frame activo
      currentIdx = Math.max(0, Math.min(currentIdx, total - 1));

      // Detectar cuál está visualmente activo (clase .active o .selected)
      const activeCard = grid.querySelector(".frameCard.active, .frameCard.selected, .frameCard.isCurrent");
      if (activeCard) {
        const idx = cards.indexOf(activeCard);
        if (idx !== -1) currentIdx = idx;
      }

      metaEl.textContent = `${currentIdx + 1} / ${total}`;
      prevBtn.disabled = currentIdx === 0;
      nextBtn.disabled = currentIdx === total - 1;
    }

    function applyIndex(idx) {
      const cards = getCards();
      if (!cards.length) return;

      currentIdx = Math.max(0, Math.min(idx, cards.length - 1));
      cards[currentIdx].click();
      updateMeta();
    }

    prevBtn.onclick = () => applyIndex(currentIdx - 1);
    nextBtn.onclick = () => applyIndex(currentIdx + 1);

    // Esperar a que el editor pinte los frames iniciales
    const waitForCards = setInterval(() => {
      if (getCards().length) {
        clearInterval(waitForCards);
        updateMeta();
      }
    }, 100);

    // FIX: MutationObserver que mantiene el contador vivo después de la carga
    // inicial — se dispara cada vez que se agrega, borra o cambia un frame.
    const obs = new MutationObserver(() => {
      requestAnimationFrame(updateMeta);
    });
    obs.observe(grid, { childList: true, subtree: true, attributes: true, attributeFilter: ["class"] });

    // También escuchar clicks en el grid para actualizar currentIdx
    // cuando el usuario toca un frame directamente
    grid.addEventListener("click", (e) => {
      const card = e.target.closest(".frameCard");
      if (!card) return;
      const cards = getCards();
      const idx = cards.indexOf(card);
      if (idx !== -1) {
        currentIdx = idx;
        requestAnimationFrame(updateMeta);
      }
    });
  }

  // ---------------------------
  // Init
  // ---------------------------
  function init() {
    const loader = document.getElementById("odMainLoader");
    if (loader) loader.style.pointerEvents = "none";

    bindModalClose();
    bindToolbarButtons();
    bindModalActions();

    bridgeAllCurrentCanvases();
    syncIconsToModal();
    setupFrameNavigation();

    // Reintento suave: el editor crea canvas/icons después
    const mo = new MutationObserver(() => {
      bridgeAllCurrentCanvases();
      syncIconsToModal();
      bindModalClose();
      bindModalActions();
      bindToolbarButtons();
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();